import React from 'react';
import './AboutPage.css';

// ── Section definitions ────────────────────────────────────────────────────────
// Each section: tint colour, icon colour, icon, heading, intro, body
const SECTIONS = [
  {
    id: 'problem',
    tint: '#fff8f0',
    iconColor: '#c2410c',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
        <circle cx="12" cy="17" r="0.8" fill="currentColor" stroke="none"/>
      </svg>
    ),
    heading: 'Why this matters',
    intro: 'Most people don\'t shop in just one place anymore.',
    body: (
      <>
        <p>You might pick up bits from different supermarkets, look for offers, or try to balance cost with quality.</p>
        <p>But even when you're being careful, it's easy to feel like:</p>
        <ul className="about-quote-list">
          <li>"Why is my shop suddenly more expensive?"</li>
          <li>"Am I actually getting the best value?"</li>
          <li>"Could I be spending less without compromising?"</li>
        </ul>
        <p>ChopMyShop is built to answer those questions.</p>
      </>
    ),
  },
  {
    id: 'what',
    tint: '#f0fdf4',
    iconColor: '#16a34a',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
        <line x1="3" y1="6" x2="21" y2="6"/>
        <path d="M16 10a4 4 0 01-8 0"/>
      </svg>
    ),
    heading: 'What ChopMyShop does',
    intro: 'Instead of focusing on individual prices, ChopMyShop looks at your whole shop.',
    body: (
      <>
        <ul className="about-feature-list">
          <li>Compare your shopping list across supermarkets</li>
          <li>See where each item is cheapest</li>
          <li>Understand your total cost before you shop</li>
        </ul>
        <p>So you can make informed choices — not guesswork decisions.</p>
      </>
    ),
  },
  {
    id: 'how',
    tint: '#eff6ff',
    iconColor: '#2563eb',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="17" width="20" height="3" rx="1"/>
        <rect x="2" y="11" width="20" height="3" rx="1"/>
        <rect x="2" y="5"  width="20" height="3" rx="1"/>
      </svg>
    ),
    heading: 'How it works',
    intro: 'We bring together store data and real-world updates from shoppers.',
    body: (
      <>
        <p>Prices are reviewed before they affect what you see, and we aim to be clear about how recent and reliable they are.</p>
        <p>Because prices can change quickly, ChopMyShop is designed to be a guide you can trust — not a guarantee.</p>
      </>
    ),
  },
  {
    id: 'built',
    tint: '#fffbeb',
    iconColor: '#b45309',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
    heading: 'Built for real households',
    intro: 'This isn\'t built in theory — it\'s built around real shopping habits.',
    body: (
      <>
        <p>Families, busy households, and everyday shoppers shape how the app evolves.</p>
        <p>That means features are tested in real life, not just designed on paper.</p>
      </>
    ),
  },
  {
    id: 'future',
    tint: '#f0fdf4',
    iconColor: '#16a34a',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
        <polyline points="17 6 23 6 23 12"/>
      </svg>
    ),
    heading: 'Where this is going',
    intro: 'This is the first version of something bigger.',
    body: (
      <>
        <p>Over time, ChopMyShop will do more than compare prices.</p>
        <p>It will help you make smarter decisions about how, where, and what you shop — with better coverage, faster updates, and more intelligent support.</p>
      </>
    ),
  },
  {
    id: 'trust',
    tint: '#f5f0fa',
    iconColor: '#55285b',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        <polyline points="9 12 11 14 15 10"/>
      </svg>
    ),
    heading: 'A note on trust',
    intro: 'We aim to provide accurate, transparent information — and continuously improve as prices and data evolve.',
    body: (
      <>
        <p>If something doesn't look right, you can report it — and that helps make the app better for everyone.</p>
        <p>ChopMyShop is built to get better over time, with the people who use it.</p>
      </>
    ),
  },
];

export default function AboutPage({ onBack }) {
  return (
    <div className="about-page">

      {/* Sticky header */}
      <div className="about-header">
        <button className="about-back-btn" onClick={onBack} aria-label="Back">
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="13 18 7 12 13 6"/>
          </svg>
        </button>
        <span className="about-header-title">About ChopMyShop</span>
      </div>

      {/* Hero card — first section elevated */}
      <div className="about-hero">
        <p className="about-hero-eyebrow">BUILT FOR REAL SHOPPING, NOT PERFECT DATA.</p>
        <h1 className="about-hero-heading">Understand what your shop really costs</h1>
        <p className="about-hero-body">Food shopping has changed.</p>
        <p className="about-hero-body">
          Prices move constantly.
          <br />
          The same basket can cost very different amounts depending on where you shop.
          <br />
          And it's not always obvious when you're actually getting value — or just reacting to what's in front of you.
        </p>
        <p className="about-hero-body about-hero-body--strong">
          ChopMyShop helps you see the full picture.
        </p>
      </div>

      {/* Section cards */}
      <div className="about-cards">
        {SECTIONS.map(s => (
          <div key={s.id} className="about-card">
            <div className="about-card-icon" style={{ color: s.iconColor }}>
              {s.icon}
            </div>
            <h2 className="about-card-heading">{s.heading}</h2>
            <p className="about-card-intro">{s.intro}</p>
            <div className="about-card-body">{s.body}</div>
          </div>
        ))}
      </div>

      <div className="about-footer">
        <p className="about-footer-text">ChopMyShop · {new Date().getFullYear()}</p>
      </div>

    </div>
  );
}
